import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import useResponse from "../../hooks/useResponse";
import trimObjectValues from "../../utils/trimObject";
import { useNavigate, useParams } from "react-router-dom";
import * as authService from "../../services/userAuthService";
import * as toast from "../../Toast";
import { Box } from "@mui/material";
import { CardContainer, Card } from "../../components/UI";
import { HeaderTitleSubtitle, SuccessAndErrorMessage } from "../../components";

import { Form, FormInput, SubmitLoadingButton } from "../../components/Forms";

import styles from "../login/login.module.css";
import { ConfirmationModal } from "../dashboard/components";
import useVisible from "../../hooks/useVisible";

// Regular expressions for OTP validation
const phoneRegex = /^\d{5}$/;

// forgot password validationSchema
const validationSchema = Yup.object().shape({
  otp: Yup.string()
    .min(5)
    .max(5)
    .test("is-otp", "Input should be a valid OTP", (value) => {
      // Check if the value matches either phone number or email pattern
      return phoneRegex.test(value);
    }),
});

const VerifyOTP = () => {
  const [state, dispatch] = useResponse();
  const { token } = useParams();
  const navigate = useNavigate();
  const [isDeleteMode, toggleDeleteMode] = useVisible(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const user = authService.validateToken(token);
    if (!user) {
      navigate(`/email-verify`);
    }
  }, [token, navigate]);

  const handleSubmit = async (loginData, formkiObject) => {
    dispatch.sendingRequst();
    loginData = trimObjectValues(loginData);
    try {
      loginData.token = token;

      const response = await authService.verifyOtp(loginData);
      const { data } = await response.data;
      setUserData(data);

      formkiObject.resetForm();
      toast.info(response.data.message);
      dispatch.successResponse(response.data.message);

      toggleDeleteMode();
    } catch (ex) {
      if (
        ex.response &&
        ex.response.status >= 400 &&
        ex.response.status <= 409
      ) {
        dispatch.gotError(ex.response.data.message);
        toast.error(ex.response.data.message);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    dispatch.sendingRequst();
    try {
      const response = await authService.deleteUserAccount(
        userData.user.id,
        userData.authToken
      );
      const data = await response.data;
      toggleDeleteMode();
      toast.success(data.message);
      dispatch.successResponse(data.message);
      navigate("/email-verify");
    } catch (ex) {
      if (
        ex.response &&
        ex.response.status >= 400 &&
        ex.response.status <= 409
      ) {
        dispatch.gotError(ex.response.data.message);
        toast.error(ex.response.data.message);
      }
    }
  };

  return (
    <div>
      <ConfirmationModal
        title="Important Notice Before Account Deletion"
        subtitle={
          state.error && <p style={{ color: "#ff0000" }}>{state.error}</p>
        }
        error="Deleting your account won’t cancel any active subscriptions. Make sure to cancel them separately to avoid future charges. All your account data will be permanently deleted. Are you sure you want to delete your account?"
        showModal={isDeleteMode}
        toggleModal={() => {
          toggleDeleteMode();
          dispatch.reset();
          navigate(`/email-verify`);
        }}
        onConfirm={handleDeleteConfirm}
        loading={state.loading}
        // error={state.error}
        ConfirmButtonText="Continue to Delete"
      />
      <CardContainer>
        <Card>
          <div className={styles.headingContainer}>
            <HeaderTitleSubtitle
              title="Delete User Account"
              subtitle="Please entered OTP to delete your account."
            />
          </div>
          <Form
            initialValues={{
              otp: "",
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              <FormInput label="OTP" name="otp" type="text" />
              <SubmitLoadingButton
                buttonText="Send OTP"
                type="submit"
                submitButton={true}
                loading={state.loading}
              />
              <SuccessAndErrorMessage
                error={state.error}
                success={state.message}
              />
            </Box>
          </Form>
        </Card>
      </CardContainer>
    </div>
  );
};

export default VerifyOTP;
